import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Line, LineChart, ResponsiveContainer, Tooltip, YAxis } from "recharts";
import {
  Breadcrumbs, Container, H2, H3, PageHero, Section, Spinner,
} from "../components/Primitives";
import { getScoreboard } from "../lib/api";
import { useSEO } from "../lib/seo";
import { CountUp, Reveal, Stagger, StaggerItem } from "../lib/motion";

export default function Scoreboard() {
  const { data, isLoading } = useQuery({ queryKey: ["scoreboard"], queryFn: getScoreboard });

  useSEO({
    title: "Our Scoreboard — Refund Rate, Response Times, No-Recommendation Rate",
    description:
      "Live numbers read from the database at request time: refund rate, median refund turnaround, 72-hour dispatch rate, and the percentage of consultations that end with no product recommendation.",
    canonical: "/about/scoreboard",
  });

  if (isLoading) return <Container className="py-20"><Spinner label="Reading the database" /></Container>;

  const { current, history, metrics } = data;

  return (
    <>
      <Container className="pt-6">
        <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "About", to: "/about" }, { label: "Scoreboard" }]} />
      </Container>

      <PageHero
        eyebrow="Updated weekly · nothing on this page is hardcoded"
        lines={["Our scoreboard"]}
        lede="Independent complaint platforms show some of the largest names in this category at 0% of complaints resolved, while the same brands show 4.7 stars on the app stores. We would rather publish a number that is merely good and true than display one that is excellent and disbelieved."
        meta={
          <p className="num mt-8 border-t border-rule pt-5 text-sm text-dim" data-testid="scoreboard-week">
            Week ending {current.week_ending} · {current.orders} orders · {current.consultations} consultations
          </p>
        }
      />

      <Section>
        <Container>
          <Stagger className="grid gap-px bg-rule lg:grid-cols-2" stagger={0.08} data-testid="scoreboard-metrics">
            {metrics.map((m, i) => (
              <StaggerItem
                key={m.key}
                className={`bg-surface p-7 sm:p-9 ${
                  metrics.length % 2 === 1 && i === metrics.length - 1 ? "lg:col-span-2" : ""
                }`}
                data-testid={`metric-${m.key}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-5">
                  <div>
                    <H3>{m.label}</H3>
                    <p className="ui mt-1.5 max-w-sm text-xs leading-[1.6] text-dim">{m.note}</p>
                  </div>
                  <p className="num text-4xl text-deep sm:text-5xl">
                    <CountUp value={current[m.key]} decimals={1} testId={`metric-value-${m.key}`} />
                    <span className="text-lg">{m.unit}</span>
                  </p>
                </div>
                <div className="mt-8">
                  <ResponsiveContainer width="100%" height={96} minWidth={0}>
                    <LineChart data={history.map((h) => ({ week: h.week_ending, v: h[m.key] }))}>
                      <YAxis hide domain={["dataMin", "dataMax"]} />
                      <Tooltip
                        contentStyle={{
                          background: "var(--c-surface)", border: "1px solid var(--c-rule)",
                          fontFamily: "Inter, sans-serif", fontSize: 12, color: "var(--c-ink)",
                        }}
                        labelFormatter={(l) => `Week ending ${l}`}
                        formatter={(v) => [`${v}${m.unit}`, m.label]}
                      />
                      <Line
                        type="monotone"
                        dataKey="v"
                        stroke="var(--c-gold)"
                        strokeWidth={1.6}
                        dot={false}
                        animationDuration={1400}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <p className="ui mt-2 text-xs text-dim">Previous twelve months</p>
              </StaggerItem>
            ))}
          </Stagger>

          <Reveal className="mt-12">
            <div className="card-s elev p-8">
              <H2 className="h2-sub">Live review counts, at this moment</H2>
              <p className="num mt-4 text-sm text-ink">
                {data.live_review_count} published reviews · {data.live_reviews_with_media_rate}% carry a
                photo or video
              </p>
              <p className="ui measure mt-4 text-xs leading-[1.7] text-dim">{data.note}</p>
            </div>
          </Reveal>
        </Container>
      </Section>
    </>
  );
}
